// KeyDiagnostic — a high-contrast screen that logs every key event the browser
// delivers (key, code, keyCode, type, repeat, timestamp). Built to find out
// exactly what the BOOX physical page-turn / volume buttons send before we
// commit to a mapping. Reachable at #/keytest.
//
// e-ink friendly: pure black-on-white (default) or white-on-black, big mono
// text, no gradients/shadows/animation.
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";

type Ev = {
  n: number;
  dt: number;       // ms since previous event
  type: string;     // keydown / keyup
  key: string;
  code: string;
  keyCode: number;
  repeat: boolean;
};

export function KeyDiagnostic() {
  const [events, setEvents] = useState<Ev[]>([]);
  const [invert, setInvert] = useState(false); // false = black on white
  const [block, setBlock] = useState(true);     // preventDefault so BOOX keys don't page the browser
  const lastT = useRef(0);
  const nRef = useRef(0);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const now = performance.now();
      const dt = lastT.current ? Math.round(now - lastT.current) : 0;
      lastT.current = now;
      nRef.current += 1;
      const ev: Ev = {
        n: nRef.current, dt, type: e.type, key: e.key || "",
        code: e.code || "", keyCode: e.keyCode, repeat: e.repeat,
      };
      setEvents((prev) => [ev, ...prev].slice(0, 60));
      if (block) { try { e.preventDefault(); } catch { /* */ } }
    };
    // capture phase so we see the event even if something else handles it
    window.addEventListener("keydown", on, true);
    window.addEventListener("keyup", on, true);
    return () => {
      window.removeEventListener("keydown", on, true);
      window.removeEventListener("keyup", on, true);
    };
  }, [block]);

  const fg = invert ? "#ffffff" : "#000000";
  const bg = invert ? "#000000" : "#ffffff";

  return (
    <div style={{ position: "fixed", inset: 0, background: bg, color: fg,
      fontFamily: "ui-monospace,Menlo,Consolas,monospace", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
        borderBottom: `2px solid ${fg}`, flexWrap: "wrap" }}>
        <Link to="/" style={{ color: fg, textDecoration: "none", fontSize: 22, fontWeight: 700 }}>‹ Back</Link>
        <span style={{ fontSize: 20, fontWeight: 700 }}>Key Test</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => setInvert((v) => !v)} style={diagBtn(fg, bg)}>Invert</button>
        <button onClick={() => setBlock((v) => !v)} style={diagBtn(fg, bg)}>{block ? "Block: ON" : "Block: OFF"}</button>
        <button onClick={() => { setEvents([]); nRef.current = 0; lastT.current = 0; }} style={diagBtn(fg, bg)}>Clear</button>
      </div>
      <div style={{ padding: "8px 12px", fontSize: 18, lineHeight: 1.4 }}>
        Press the BOOX side buttons / volume / arrows. Newest at top.
        If nothing appears, the browser isn't receiving that key (the wrapper APK path is next).
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 16px" }}>
        {events.length === 0 && (
          <div style={{ fontSize: 20, opacity: 0.6, padding: "20px 0" }}>…waiting for key events…</div>
        )}
        {events.map((e) => (
          <div key={e.n} style={{ fontSize: 19, padding: "8px 0", borderBottom: `1px solid ${fg}33` }}>
            <div style={{ fontWeight: 700 }}>
              #{e.n} · {e.type}{e.repeat ? " (repeat)" : ""} · +{e.dt}ms
            </div>
            <div>key=<b>{JSON.stringify(e.key)}</b>&nbsp; code=<b>{e.code || "—"}</b>&nbsp; keyCode=<b>{e.keyCode}</b></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function diagBtn(fg: string, bg: string): CSSProperties {
  return { fontSize: 16, fontWeight: 700, color: bg, background: fg, border: `2px solid ${fg}`,
    borderRadius: 6, padding: "8px 12px", minHeight: 44, cursor: "pointer" };
}

export default KeyDiagnostic;
